/**
 * CalendarProvider — 캘린더 모듈 상태 Provider
 *
 * AppLayout 레벨에서 감싸져서 LeftMenuCalendar와 CalendarPage가
 * 동일한 Context를 공유합니다.
 *
 * 합의 문서: .agent/plans/calendar_phase1_design.md
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { t } from '@lingui/macro';
import { CalendarContext, DateRange } from '../../context/CalendarContext';
import {
  CalendarEvent,
  CalendarViewMode,
  CalendarFilters,
  DEFAULT_FILTERS,
  getUserCalendarView,
  setUserCalendarView,
  loadCalendarFilters,
  saveCalendarFilters,
} from '../../models/CalendarEvent';
import { fetchEvents, fetchUserAccessibleTaskDeadlines } from '../../services/CalendarService';
import { useCalendarRangeData, useCalendarUserMeta, CalendarRangeData } from './useCalendarRangeData';
import { useAppUIContext } from '../../context/AppUIContext';

interface Props {
  children: React.ReactNode;
}

/** 뷰 모드 + 기준일로 표시 range 계산 */
function computeRange(mode: CalendarViewMode, anchor: Date): DateRange {
  const start = new Date(anchor);
  const end = new Date(anchor);
  switch (mode) {
    case 'month': {
      // 월의 1일 ~ 다음달 1일 (캘린더 뷰는 보통 전월 말 + 다음월 초까지 포함)
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      // 1주일 전부터 (월 시작 주의 일요일까지 보이도록)
      start.setDate(start.getDate() - 7);
      end.setMonth(end.getMonth() + 1);
      end.setDate(1);
      end.setHours(0, 0, 0, 0);
      end.setDate(end.getDate() + 14); // 다음달 초까지 여유
      break;
    }
    case 'week': {
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 7);
      break;
    }
    case 'day': {
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 1);
      end.setHours(0, 0, 0, 0);
      break;
    }
    case 'agenda':
    default: {
      // 어젠다: anchor 기준 30일 미래
      start.setHours(0, 0, 0, 0);
      end.setDate(start.getDate() + 30);
      break;
    }
  }
  return { start, end };
}

const CalendarProvider: React.FC<Props> = ({ children }) => {
  const { activeWorkspaceId, setActiveWorkspaceId, activeModule, workspaces, rooms } = useAppUIContext();

  // 새로고침 등으로 activeWorkspaceId가 null인데 workspaces가 있으면 자동 선택
  useEffect(() => {
    if (!activeWorkspaceId && workspaces.length > 0) {
      setActiveWorkspaceId(workspaces[0].id);
    }
  }, [activeWorkspaceId, workspaces]);

  const [viewMode, setViewModeState] = useState<CalendarViewMode>(() => getUserCalendarView() ?? 'month');
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [filters, setFiltersState] = useState<CalendarFilters>(() => loadCalendarFilters());

  const range = useMemo(() => computeRange(viewMode, anchorDate), [viewMode, anchorDate]);
  const setRange = useCallback((_r: DateRange) => {
    // range 는 viewMode + anchorDate 에서 자동 계산되므로 외부 setter 는 무시.
    // 인터페이스 유지용으로만 존재.
  }, []);

  /** 뷰 모드 변경 + 영구 저장 */
  const setViewMode = useCallback((mode: CalendarViewMode) => {
    setViewModeState(mode);
    setUserCalendarView(mode);
  }, []);

  /** 필터 변경 + 영구 저장 */
  const setFilters = useCallback((next: CalendarFilters) => {
    setFiltersState(next);
    saveCalendarFilters(next);
  }, []);

  // 사용자가 멤버인 채널 id 모음 (Tasks UNION 용)
  const myChannelIds = useMemo(() => {
    if (!activeWorkspaceId) return [] as string[];
    return rooms
      .filter((r) => r.workspaceId === activeWorkspaceId
        && (r.visibility === 'listed' || r.visibility === 'unlisted')
        && !r.isOneToOne)
      .map((r) => r.id);
  }, [rooms, activeWorkspaceId]);

  // 범위 데이터 fetcher — SWR/TTL/캐시/늦은응답 가드는 useCalendarRangeData 가 담당 (채널 캘린더와 공용)
  const fetchRange = useCallback(async (r: DateRange): Promise<CalendarRangeData> => {
    const [list, tasks] = await Promise.all([
      fetchEvents({
        workspaceId: activeWorkspaceId!,
        rangeStart: r.start.toISOString(),
        rangeEnd: r.end.toISOString(),
      }),
      fetchUserAccessibleTaskDeadlines({
        workspaceId: activeWorkspaceId!,
        channelIds: myChannelIds,
        rangeStart: r.start.toISOString(),
        rangeEnd: r.end.toISOString(),
      }).catch((e) => { console.warn('[CalendarProvider] task 마감일 로드 실패:', e); return [] as CalendarEvent[]; }),
    ]);
    return { events: list, taskEvents: tasks };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId, myChannelIds.join(',')]);

  // 캘린더 모듈이 활성일 때만 로드 (기존 동작 유지)
  const calendarActive = activeModule === 'calendar';
  const {
    events, setEvents, taskEvents, loading, setLoading, error, refresh,
  } = useCalendarRangeData({
    scopeKey: `main|${activeWorkspaceId}|${myChannelIds.join(',')}`,
    range,
    enabled: !!activeWorkspaceId && calendarActive,
    fetchRange,
    errorFallback: t`Failed to load events`,
  });

  // 사용자 단위 메타(참석 ID·리마인더 맵) — 채널/메인 캘린더가 모듈 캐시를 공유 (중복 fetch 없음)
  const { attendingEventIds, remindersMap, refreshUserMeta } = useCalendarUserMeta(calendarActive);

  /** 일정 목록 새로고침 — 뮤테이션 후 강제 갱신 (사용자 캐시 전체 무효화 포함) */
  const refreshEvents = useCallback(async () => {
    await Promise.all([refresh(), refreshUserMeta()]);
  }, [refresh, refreshUserMeta]);

  const contextValue = {
    viewMode,
    setViewMode,
    range,
    setRange,
    anchorDate,
    setAnchorDate,
    events,
    setEvents,
    taskEvents,
    remindersMap,
    selectedEvent,
    setSelectedEvent,
    filters,
    setFilters,
    loading,
    setLoading,
    refreshEvents,
    attendingEventIds,
    error,
  };

  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
};

export default CalendarProvider;
